import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Button,
} from '@mui/material';
import {
  Build,
  DirectionsCar,
  CheckCircle,
  Cancel,
  Business,
  Person,
  CalendarMonth,
  DateRange,
  Warning,
  Edit as EditIcon,
  AdminPanelSettings,
} from '@mui/icons-material';
import { collection, query, where, getDocs, orderBy, Timestamp, addDoc } from 'firebase/firestore';
import { MainLayout } from '../components/layout/MainLayout';
import { db } from '../lib/firebase';
import { Maintenance, MonthlyMaintenanceStats } from '../types/maintenance';
import { Operation } from '../types/operation';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../lib/firebase';
import { format, startOfWeek, endOfWeek, addWeeks } from 'date-fns';
import { ko } from 'date-fns/locale';
import { MaintenanceFormDialog } from '../components/maintenance/MaintenanceFormDialog';

type ViewType = 'weekly' | 'monthly';

interface MissingMaintenanceDriver {
  driverName: string;
  driverUid: string | null;
  vehicleNumber: string;
  organizationId: string;
}

// Feature Flag: 노선 관리자의 점검 작성 기능 활성화 여부
// true: 활성화, false: 비활성화 (정책상 임시 비활성화)
const ENABLE_ADMIN_MAINTENANCE_WRITE = false;

export const MaintenancePage: React.FC = () => {
  const { adminType } = useAuth();
  const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState<ViewType>('weekly');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [missingDrivers, setMissingDrivers] = useState<MissingMaintenanceDriver[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<MissingMaintenanceDriver | null>(null);

  // 최근 12주 목록 생성 (월요일 시작, 토요일 종료)
  const weekOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const targetDate = addWeeks(now, -i);
      const weekStart = startOfWeek(targetDate, { weekStartsOn: 1 }); // 월요일 시작
      const weekEnd = endOfWeek(targetDate, { weekStartsOn: 1 }); // 월요일 기준 주말
      // 토요일로 조정
      const saturday = new Date(weekEnd);
      saturday.setDate(weekEnd.getDate() - 1);

      const value = format(weekStart, 'yyyyMMdd');
      const label = `${format(weekStart, 'MM/dd', { locale: ko })} ~ ${format(saturday, 'MM/dd', { locale: ko })}`;
      options.push({ value, label });
    }

    return options;
  }, []);

  // 최근 12개월 목록 생성
  const monthOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = format(date, 'yyyyMM');
      const label = format(date, 'yyyy년 MM월', { locale: ko });
      options.push({ value, label });
    }

    return options;
  }, []);

  // 현재 기간 기본 선택
  useEffect(() => {
    if (viewType === 'weekly' && weekOptions.length > 0) {
      setSelectedPeriod(weekOptions[0].value);
    } else if (viewType === 'monthly' && monthOptions.length > 0) {
      setSelectedPeriod(monthOptions[0].value);
    }
  }, [viewType, weekOptions, monthOptions]);

  // 점검 기록 조회
  useEffect(() => {
    const fetchMaintenances = async () => {
      if (!selectedPeriod) return;

      setLoading(true);
      try {
        let startDate: Date;
        let endDate: Date;

        if (viewType === 'weekly') {
          // 주간: yyyyMMdd 형식에서 월요일 추출 후 토요일까지
          const year = parseInt(selectedPeriod.substring(0, 4));
          const month = parseInt(selectedPeriod.substring(4, 6)) - 1;
          const day = parseInt(selectedPeriod.substring(6, 8));

          startDate = new Date(year, month, day); // 월요일
          endDate = new Date(year, month, day + 6); // 토요일
          // 토요일 23:59:59로 설정
          endDate.setHours(23, 59, 59, 999);
        } else {
          // 월간: yyyyMM 형식
          const year = parseInt(selectedPeriod.substring(0, 4));
          const month = parseInt(selectedPeriod.substring(4, 6)) - 1;

          startDate = new Date(year, month, 1);
          endDate = new Date(year, month + 1, 1);
        }

        const startTimestamp = Timestamp.fromDate(startDate);
        const endTimestamp = Timestamp.fromDate(endDate);

        let q = query(
          collection(db, 'maintenance'),
          where('createdAt', '>=', startTimestamp),
          where('createdAt', '<', endTimestamp),
          orderBy('createdAt', 'desc')
        );

        // 권한별 필터링
        if (adminType === 'route1') {
          q = query(
            collection(db, 'maintenance'),
            where('createdAt', '>=', startTimestamp),
            where('createdAt', '<', endTimestamp),
            where('organizationId', '==', '부산은성관광'),
            orderBy('createdAt', 'desc')
          );
        } else if (adminType === 'route2') {
          q = query(
            collection(db, 'maintenance'),
            where('createdAt', '>=', startTimestamp),
            where('createdAt', '<', endTimestamp),
            where('organizationId', '==', '뉴대림고속관광'),
            orderBy('createdAt', 'desc')
          );
        }

        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Maintenance[];

        setMaintenances(data);
      } catch (error) {
        console.error('Error fetching maintenances:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMaintenances();
  }, [selectedPeriod, viewType, adminType]);

  // 미작성 운전자 조회 (주간 뷰일 때만)
  useEffect(() => {
    const fetchMissingDrivers = async () => {
      if (!selectedPeriod || viewType !== 'weekly') {
        setMissingDrivers([]);
        return;
      }

      try {
        const year = parseInt(selectedPeriod.substring(0, 4));
        const month = parseInt(selectedPeriod.substring(4, 6)) - 1;
        const day = parseInt(selectedPeriod.substring(6, 8));

        const startDate = new Date(year, month, day); // 월요일
        const endDate = new Date(year, month, day + 6); // 토요일
        endDate.setHours(23, 59, 59, 999);

        // 날짜 범위 생성 (YYYY-MM-DD 형식)
        const dateRange: string[] = [];
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          dateRange.push(format(d, 'yyyy-MM-dd'));
        }

        // operations 컬렉션에서 해당 주간 운행 기록 조회
        const operationsPromises = dateRange.map(async (date) => {
          const startDocId = `${date}_`;
          const endDocId = `${date}_\uf8ff`;

          let q = query(
            collection(db, 'operations'),
            orderBy('__name__'),
            where('__name__', '>=', startDocId),
            where('__name__', '<', endDocId)
          );

          const snapshot = await getDocs(q);
          return snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as Operation[];
        });

        const operationsResults = await Promise.all(operationsPromises);
        const allOperations = operationsResults.flat();

        console.log('=== 점검 미작성 운전자 분석 ===');
        console.log('조회 기간:', dateRange);
        console.log('전체 운행 기록 수:', allOperations.length);
        console.log('adminType:', adminType);

        // 권한별 필터링 적용 (routeName 기준)
        const filteredOperations = allOperations.filter((op) => {
          if (adminType === 'route1') {
            // route1: routeName이 "강서구청"으로 시작
            return op.routeName?.startsWith('강서구청');
          } else if (adminType === 'route2') {
            // route2: routeName이 "하단"으로 시작
            return op.routeName?.startsWith('하단');
          }
          return true; // super admin은 모두 조회
        });

        console.log('권한별 필터링 후 운행 기록 수:', filteredOperations.length);

        // 운전자별로 그룹화 (driverName이 있는 것만)
        const driverMap = new Map<
          string,
          { driverUid: string | null; vehicleNumber: string; organizationId: string }
        >();
        filteredOperations.forEach((op) => {
          if (op.driverName && op.driverName.trim() !== '') {
            // 동일 운전자가 여러 차량을 운전한 경우, 가장 최근 차량 기준
            if (!driverMap.has(op.driverName)) {
              // 조직 판단 (routeName 기준)
              let org = '';
              if (op.routeName?.startsWith('강서구청')) {
                org = '부산은성관광';
              } else if (op.routeName?.startsWith('하단')) {
                org = '뉴대림고속관광';
              }

              driverMap.set(op.driverName, {
                driverUid: op.driverUid || null, // operations에서 driverUid가 있으면 사용
                vehicleNumber: op.vehicleNumber,
                organizationId: org,
              });
            }
          }
        });

        console.log('해당 주간 운전한 운전자 목록:');
        driverMap.forEach((info, driverName) => {
          console.log(
            `  - ${driverName} (UID: ${info.driverUid || 'N/A'}, ${info.vehicleNumber}, ${info.organizationId})`
          );
        });

        // driverUid가 없는 운전자에 대해 driver_profiles 조회
        const driverUidPromises: Promise<void>[] = [];
        driverMap.forEach((info, driverName) => {
          if (!info.driverUid) {
            const promise = fetchDriverUid(driverName, info.vehicleNumber, info.organizationId).then(
              (uid) => {
                if (uid) {
                  // driverMap 업데이트
                  driverMap.set(driverName, { ...info, driverUid: uid });
                }
              }
            );
            driverUidPromises.push(promise);
          }
        });

        // 모든 driverUid 조회 완료 대기
        await Promise.all(driverUidPromises);

        console.log('driverUid 보완 후 운전자 목록:');
        driverMap.forEach((info, driverName) => {
          console.log(
            `  - ${driverName} (UID: ${info.driverUid || 'N/A'}, ${info.vehicleNumber}, ${info.organizationId})`
          );
        });

        // maintenance 컬렉션에서 해당 주간에 점검 작성한 운전자 추출
        // driverUid가 있으면 UID 기준, 없으면 driverName 기준
        const maintenanceDriverUids = new Set(
          maintenances.filter((m) => m.driverUid).map((m) => m.driverUid!)
        );
        const maintenanceDriverNames = new Set(
          maintenances
            .filter((m) => !m.driverUid && m.driverName && m.driverName.trim() !== '')
            .map((m) => m.driverName)
        );

        console.log('점검 작성한 운전자 (UID):', Array.from(maintenanceDriverUids));
        console.log('점검 작성한 운전자 (이름):', Array.from(maintenanceDriverNames));

        // 미작성 운전자 계산
        const missing: MissingMaintenanceDriver[] = [];
        driverMap.forEach((info, driverName) => {
          // driverUid가 있으면 UID로 비교, 없으면 이름으로 비교
          const hasCompleted = info.driverUid
            ? maintenanceDriverUids.has(info.driverUid)
            : maintenanceDriverNames.has(driverName);

          if (!hasCompleted) {
            missing.push({
              driverName,
              driverUid: info.driverUid,
              vehicleNumber: info.vehicleNumber,
              organizationId: info.organizationId,
            });
          }
        });

        console.log('점검 미작성 운전자:', missing);
        console.log('===========================');

        setMissingDrivers(missing);
      } catch (error) {
        console.error('Error fetching missing drivers:', error);
        setMissingDrivers([]);
      }
    };

    fetchMissingDrivers();
  }, [selectedPeriod, viewType, adminType, maintenances]);

  // 통계 계산
  const statistics = useMemo<MonthlyMaintenanceStats>(() => {
    const uniqueVehicles = new Set(maintenances.map((m) => m.vehicleNumber));

    const allChecksPassed = maintenances.filter(
      (m) =>
        m.engineChecked &&
        m.powerTrainChecked &&
        m.brakeChecked &&
        m.steeringChecked &&
        m.lightingChecked
    ).length;

    const partialChecksPassed = maintenances.filter(
      (m) =>
        !(
          m.engineChecked &&
          m.powerTrainChecked &&
          m.brakeChecked &&
          m.steeringChecked &&
          m.lightingChecked
        )
    ).length;

    return {
      totalRecords: maintenances.length,
      totalVehicles: uniqueVehicles.size,
      allChecksPassed,
      partialChecksPassed,
    };
  }, [maintenances]);

  // 점검 항목 통과 여부 체크
  const isAllChecksPassed = (maintenance: Maintenance): boolean => {
    return (
      maintenance.engineChecked &&
      maintenance.powerTrainChecked &&
      maintenance.brakeChecked &&
      maintenance.steeringChecked &&
      maintenance.lightingChecked
    );
  };

  // driver_profiles에서 driverName + vehicleNumber로 UID 조회
  const fetchDriverUid = async (
    driverName: string,
    vehicleNumber: string,
    organizationId: string
  ): Promise<string | null> => {
    try {
      // 이름과 차량번호만으로 조회 (organization_name 조건 제거)
      const profilesQuery = query(
        collection(db, 'driver_profiles'),
        where('name', '==', driverName),
        where('vehicleNumber', '==', vehicleNumber)
      );

      const snapshot = await getDocs(profilesQuery);

      if (!snapshot.empty) {
        const uid = snapshot.docs[0].data().uid;
        const foundOrg = snapshot.docs[0].data().organization_name;
        console.log(`Found driverUid for ${driverName}: ${uid} (organization: ${foundOrg})`);

        // 조직이 다르면 경고 로그 출력
        if (foundOrg !== organizationId) {
          console.warn(
            `⚠️ Organization mismatch: expected "${organizationId}", found "${foundOrg}" for driver ${driverName}`
          );
        }

        return uid;
      }

      console.log(`No driver profile found for ${driverName} (${vehicleNumber})`);
      return null;
    } catch (error) {
      console.error('Error fetching driver UID:', error);
      return null;
    }
  };

  // 점검 작성 버튼 클릭 핸들러
  const handleOpenDialog = (driver: MissingMaintenanceDriver) => {
    setSelectedDriver(driver);
    setDialogOpen(true);
  };

  // 점검 작성 제출 핸들러
  const handleSubmitMaintenance = async (checkData: {
    engineChecked: boolean;
    powerTrainChecked: boolean;
    brakeChecked: boolean;
    steeringChecked: boolean;
    lightingChecked: boolean;
  }) => {
    if (!selectedDriver) return;

    try {
      const now = new Date();
      const inspectionDate = format(now, 'yyyy-MM-dd');

      // 1. driverUid 확보 (selectedDriver에 이미 있거나, 없으면 조회)
      let driverUid = selectedDriver.driverUid;
      if (!driverUid) {
        driverUid = await fetchDriverUid(
          selectedDriver.driverName,
          selectedDriver.vehicleNumber,
          selectedDriver.organizationId
        );
      }

      // 2. 중복 체크: 동일 날짜에 이미 점검기록이 있는지 확인
      let duplicateQuery;
      if (driverUid) {
        // driverUid가 있으면 UID 기준으로 중복 체크
        duplicateQuery = query(
          collection(db, 'maintenance'),
          where('inspectionDate', '==', inspectionDate),
          where('driverUid', '==', driverUid)
        );
      } else {
        // driverUid가 없으면 driverName 기준으로 중복 체크
        duplicateQuery = query(
          collection(db, 'maintenance'),
          where('inspectionDate', '==', inspectionDate),
          where('driverName', '==', selectedDriver.driverName)
        );
      }

      const duplicateSnapshot = await getDocs(duplicateQuery);

      if (!duplicateSnapshot.empty) {
        alert(`${selectedDriver.driverName} 운전자의 ${inspectionDate} 점검기록이 이미 존재합니다.`);
        throw new Error('Duplicate maintenance record');
      }

      // 3. Firestore에 점검기록 저장
      const maintenanceData: any = {
        vehicleNumber: selectedDriver.vehicleNumber,
        driverName: selectedDriver.driverName,
        organizationId: selectedDriver.organizationId,
        inspectionDate,
        createdAt: Timestamp.fromDate(now),
        createdByAdmin: true,
        adminUid: auth.currentUser?.uid,
        engineChecked: checkData.engineChecked,
        powerTrainChecked: checkData.powerTrainChecked,
        brakeChecked: checkData.brakeChecked,
        steeringChecked: checkData.steeringChecked,
        lightingChecked: checkData.lightingChecked,
      };

      // driverId가 있을 때만 필드에 추가
      if (driverUid) {
        maintenanceData.driverId = driverUid;
      }

      await addDoc(collection(db, 'maintenance'), maintenanceData);

      console.log('✅ 점검기록 저장 완료:', {
        driverName: selectedDriver.driverName,
        driverId: driverUid || 'N/A',
        createdByAdmin: true,
        adminUid: auth.currentUser?.uid,
      });

      // 성공 후 maintenances 목록 새로고침을 위해 다시 조회
      // useEffect의 의존성에 의해 자동으로 미작성 운전자 목록도 갱신됨
      const year = parseInt(selectedPeriod.substring(0, 4));
      const month = parseInt(selectedPeriod.substring(4, 6)) - 1;
      const day = parseInt(selectedPeriod.substring(6, 8));

      const startDate = new Date(year, month, day);
      const endDate = new Date(year, month, day + 6);
      endDate.setHours(23, 59, 59, 999);

      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);

      let q = query(
        collection(db, 'maintenance'),
        where('createdAt', '>=', startTimestamp),
        where('createdAt', '<', endTimestamp),
        orderBy('createdAt', 'desc')
      );

      if (adminType === 'route1') {
        q = query(
          collection(db, 'maintenance'),
          where('createdAt', '>=', startTimestamp),
          where('createdAt', '<', endTimestamp),
          where('organizationId', '==', '부산은성관광'),
          orderBy('createdAt', 'desc')
        );
      } else if (adminType === 'route2') {
        q = query(
          collection(db, 'maintenance'),
          where('createdAt', '>=', startTimestamp),
          where('createdAt', '<', endTimestamp),
          where('organizationId', '==', '뉴대림고속관광'),
          orderBy('createdAt', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Maintenance[];

      setMaintenances(data);
      alert('점검기록이 성공적으로 저장되었습니다.');
    } catch (error) {
      console.error('Error creating maintenance:', error);
      alert('점검기록 저장에 실패했습니다. 다시 시도해주세요.');
      throw error;
    }
  };

  return (
    <MainLayout>
      <Box>
        {/* 헤더 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Build sx={{ fontSize: 32, color: '#667eea' }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                점검기록
              </Typography>
              <Typography variant="body2" color="text.secondary">
                차량 정기 점검 기록을 조회합니다.
              </Typography>
            </Box>
          </Box>

          {/* 뷰 전환 및 기간 선택 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* 뷰 타입 선택 */}
            <ToggleButtonGroup
              value={viewType}
              exclusive
              onChange={(e, newViewType) => {
                if (newViewType !== null) {
                  setViewType(newViewType);
                }
              }}
              size="small"
            >
              <ToggleButton value="weekly">
                <DateRange sx={{ fontSize: 18, mr: 0.5 }} />
                주간
              </ToggleButton>
              <ToggleButton value="monthly">
                <CalendarMonth sx={{ fontSize: 18, mr: 0.5 }} />
                월간
              </ToggleButton>
            </ToggleButtonGroup>

            {/* 기간 선택 */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{viewType === 'weekly' ? '조회 주' : '조회 월'}</InputLabel>
              <Select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                label={viewType === 'weekly' ? '조회 주' : '조회 월'}
              >
                {(viewType === 'weekly' ? weekOptions : monthOptions).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* 통계 카드 */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Build sx={{ fontSize: 32, color: '#667eea' }} />
                  <Typography variant="h6" fontWeight={600}>
                    총 점검 기록
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="primary">
                  {statistics.totalRecords}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  이번 달 점검 횟수입니다.
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <DirectionsCar sx={{ fontSize: 32, color: '#10b981' }} />
                  <Typography variant="h6" fontWeight={600}>
                    점검 차량 수
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#10b981' }}>
                  {statistics.totalVehicles}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  점검한 차량 수입니다.
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <CheckCircle sx={{ fontSize: 32, color: '#10b981' }} />
                  <Typography variant="h6" fontWeight={600}>
                    전체 통과
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#10b981' }}>
                  {statistics.allChecksPassed}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {statistics.totalRecords > 0
                    ? ((statistics.allChecksPassed / statistics.totalRecords) * 100).toFixed(1)
                    : 0}
                  % 통과율
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Cancel sx={{ fontSize: 32, color: '#f59e0b' }} />
                  <Typography variant="h6" fontWeight={600}>
                    일부 통과
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#f59e0b' }}>
                  {statistics.partialChecksPassed}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  일부 항목만 통과했습니다.
                </Typography>
              </Paper>
            </Box>

            {/* 점검 기록 테이블 */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                점검 기록 목록
              </Typography>

              {maintenances.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography color="text.secondary">점검 기록이 없습니다.</Typography>
                </Box>
              ) : (
                <TableContainer sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>점검일</TableCell>
                        <TableCell>차량 번호</TableCell>
                        <TableCell>운전자</TableCell>
                        <TableCell>소속</TableCell>
                        <TableCell align="center">엔진</TableCell>
                        <TableCell align="center">동력전달</TableCell>
                        <TableCell align="center">제동</TableCell>
                        <TableCell align="center">조향</TableCell>
                        <TableCell align="center">등화</TableCell>
                        <TableCell align="center">전체 결과</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {maintenances.map((maintenance) => (
                        <TableRow key={maintenance.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {format(maintenance.createdAt.toDate(), 'yyyy-MM-dd (E)', {
                                locale: ko,
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <DirectionsCar sx={{ fontSize: 18, color: '#10b981' }} />
                              <Typography variant="body2" fontWeight={600}>
                                {maintenance.vehicleNumber}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Person sx={{ fontSize: 18, color: '#667eea' }} />
                              <Typography variant="body2">{maintenance.driverName}</Typography>
                              {maintenance.createdByAdmin && (
                                <Chip
                                  label="관리자 작성"
                                  size="small"
                                  color="info"
                                  icon={<AdminPanelSettings sx={{ fontSize: 14 }} />}
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Business sx={{ fontSize: 18, color: '#764ba2' }} />
                              <Typography variant="body2">{maintenance.organizationId}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {maintenance.engineChecked ? (
                              <CheckCircle sx={{ fontSize: 20, color: '#10b981' }} />
                            ) : (
                              <Cancel sx={{ fontSize: 20, color: '#ef4444' }} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {maintenance.powerTrainChecked ? (
                              <CheckCircle sx={{ fontSize: 20, color: '#10b981' }} />
                            ) : (
                              <Cancel sx={{ fontSize: 20, color: '#ef4444' }} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {maintenance.brakeChecked ? (
                              <CheckCircle sx={{ fontSize: 20, color: '#10b981' }} />
                            ) : (
                              <Cancel sx={{ fontSize: 20, color: '#ef4444' }} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {maintenance.steeringChecked ? (
                              <CheckCircle sx={{ fontSize: 20, color: '#10b981' }} />
                            ) : (
                              <Cancel sx={{ fontSize: 20, color: '#ef4444' }} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {maintenance.lightingChecked ? (
                              <CheckCircle sx={{ fontSize: 20, color: '#10b981' }} />
                            ) : (
                              <Cancel sx={{ fontSize: 20, color: '#ef4444' }} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={isAllChecksPassed(maintenance) ? '전체 통과' : '일부 통과'}
                              color={isAllChecksPassed(maintenance) ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* 점검 미작성 운전자 테이블 (주간 뷰일 때만 표시) */}
            {viewType === 'weekly' && (
              <Paper sx={{ p: 3, mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Warning sx={{ fontSize: 28, color: '#f59e0b' }} />
                  <Typography variant="h6" fontWeight={600}>
                    점검 미작성 운전자
                  </Typography>
                  {missingDrivers.length > 0 && (
                    <Chip
                      label={`${missingDrivers.length}명`}
                      color="warning"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>

                {missingDrivers.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#f9fafb', borderRadius: 2 }}>
                    <CheckCircle sx={{ fontSize: 48, color: '#10b981', mb: 2 }} />
                    <Typography color="text.secondary" fontWeight={500}>
                      모든 운전자가 점검을 완료했습니다.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>운전자명</TableCell>
                          <TableCell>차량 번호</TableCell>
                          <TableCell>소속</TableCell>
                          <TableCell align="center">작업</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {missingDrivers.map((driver, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Person sx={{ fontSize: 18, color: '#667eea' }} />
                                <Typography variant="body2" fontWeight={600}>
                                  {driver.driverName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DirectionsCar sx={{ fontSize: 18, color: '#10b981' }} />
                                <Typography variant="body2">{driver.vehicleNumber}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Business sx={{ fontSize: 18, color: '#764ba2' }} />
                                <Typography variant="body2">{driver.organizationId}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              {(adminType === 'route1' || adminType === 'route2') &&
                              ENABLE_ADMIN_MAINTENANCE_WRITE ? (
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<EditIcon />}
                                  onClick={() => handleOpenDialog(driver)}
                                  sx={{
                                    bgcolor: '#667eea',
                                    '&:hover': { bgcolor: '#5568d3' },
                                  }}
                                >
                                  점검 작성
                                </Button>
                              ) : adminType === 'route1' || adminType === 'route2' ? (
                                <Chip
                                  label="기능 일시 중지"
                                  size="small"
                                  variant="outlined"
                                  sx={{ color: '#94a3b8' }}
                                />
                              ) : (
                                <Chip
                                  label="노선관리자 전용"
                                  size="small"
                                  variant="outlined"
                                  sx={{ color: 'text.secondary' }}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            )}
          </>
        )}

        {/* 점검 작성 다이얼로그 */}
        {selectedDriver && (
          <MaintenanceFormDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            driverName={selectedDriver.driverName}
            vehicleNumber={selectedDriver.vehicleNumber}
            organizationId={selectedDriver.organizationId}
            onSubmit={handleSubmitMaintenance}
          />
        )}
      </Box>
    </MainLayout>
  );
};
